import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  HiOutlineClipboardList,
  HiOutlineLockClosed,
  HiOutlineSparkles,
  HiOutlinePlay,
  HiOutlineCheckCircle,
  HiOutlineAcademicCap,
  HiOutlineClock,
  HiOutlineUserGroup,
} from "react-icons/hi";
import { FaTelegramPlane } from "react-icons/fa";
import ReactPlayer from "react-player";
import { useAuthContext } from "../../context/AuthContext";
import Axios from "../../Axios";
import Layout from "../../utils/Layouts/Layout/Layout";
import PayModal from "../../modals/PayModal/PayModal";
import Popup from "../../utils/Popup/Popup";
import Header from "../../utils/Header/Header";
import "./courseDetail.scss";

const noFile = require("../../assets/no-file.png");
const teacherFallback = require("../../assets/teacher.jpg");
const defaultPreviewVideo = "https://www.youtube.com/watch?v=hRGMe4kEST8";

const decodeHtmlEntities = (value = "") =>
  String(value)
    .replace(/&amp;/gi, "&")
    .replace(/&#x2F;/gi, "/")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

const getPlayableVideoURL = (rawValue) => {
  if (!rawValue) return null;

  const normalized = decodeHtmlEntities(String(rawValue).trim());

  if (/^https?:\/\//i.test(normalized)) {
    return normalized.split(/\s+/)[0];
  }

  const srcMatch = normalized.match(/src=["']([^"']+)["']/i);
  if (srcMatch?.[1]) {
    return decodeHtmlEntities(srcMatch[1].trim());
  }

  const directUrlMatch = normalized.match(/https?:\/\/[^\s"'<>]+/i);
  if (directUrlMatch?.[0]) {
    return decodeHtmlEntities(directUrlMatch[0].trim());
  }

  return null;
};

const VIMEO_ORIGIN = "https://player.vimeo.com";

// react-player -> @vimeo/player нь тоглуулагчийг эхлүүлэхдээ
// vimeo.com/api/oembed.json руу хүсэлт явуулж, бичлэгийн нэр, зохиогч,
// account төрөл, upload огноо, thumbnail зэргийг Network таб дээр ил гаргадаг.
// Иймд iframe-ийг өөрсдөө үүсгэж, зөвхөн video ID-г л ил үлдээнэ.
const getVimeoEmbed = (rawValue) => {
  const url = getPlayableVideoURL(rawValue);
  if (!url) return null;

  // vimeo.com/ID, player.vimeo.com/video/ID, unlisted vimeo.com/ID/HASH
  const idMatch = url.match(
    /(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)(?:\/([0-9a-z]+))?/i
  );
  if (!idMatch) return null;

  const queryHashMatch = url.match(/[?&]h=([0-9a-z]+)/i);
  const hash = queryHashMatch?.[1] || idMatch[2] || null;

  const params = new URLSearchParams({
    badge: "0",
    byline: "0",
    portrait: "0",
    title: "0",
    pip: "0",
    dnt: "1",
  });

  if (hash) {
    params.set("h", hash);
  }

  return `${VIMEO_ORIGIN}/video/${idMatch[1]}?${params.toString()}`;
};

// Vimeo-гийн iframe нь postMessage API-тай тул play/pause төлөвийг
// player.js ачаалахгүйгээр сонсож болно.
const VimeoFrame = ({ src, title = "video", onPlay, onPause, onEnded }) => {
  const frameRef = useRef(null);
  const handlersRef = useRef({});

  handlersRef.current = { onPlay, onPause, onEnded };

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;

    const post = (method, value) => {
      frame.contentWindow?.postMessage({ method, value }, VIMEO_ORIGIN);
    };

    const subscribe = () => {
      post("addEventListener", "play");
      post("addEventListener", "pause");
      post("addEventListener", "ended");
    };

    const messageHandler = (e) => {
      if (e.origin !== VIMEO_ORIGIN) return;

      let payload = e.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch (_) {
          return;
        }
      }

      const { onPlay: p, onPause: pa, onEnded: en } = handlersRef.current;

      if (payload?.event === "play") p?.();
      if (payload?.event === "pause") pa?.();
      if (payload?.event === "ended") en?.();
    };

    frame.addEventListener("load", subscribe);
    window.addEventListener("message", messageHandler);

    return () => {
      frame.removeEventListener("load", subscribe);
      window.removeEventListener("message", messageHandler);
    };
  }, [src]);

  return (
    <iframe
      ref={frameRef}
      src={src}
      title={title}
      width="100%"
      height="100%"
      frameBorder="0"
      allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
      allowFullScreen
    />
  );
};

const CourseDetail = () => {
  const { id } = useParams();
  const { isLogin, authConfig } = useAuthContext();

  const lessonsRef = useRef(null);

  const [data, setData] = useState({});
  const [lessons, setLessons] = useState([]);
  const [isPayed, setIsPayed] = useState(false);
  const [userRoleID, setUserRoleID] = useState(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);

  const [visiblePay, setVisiblePay] = useState(false);
  const [visibleNotPay, setVisibleNotPay] = useState(false);
  const [visibleLessons, setVisibleLessons] = useState(false);
  const [activeIdx, setActiveIdx] = useState(null);

  useEffect(() => {
    const menuHandler = (e) => {
      if (!lessonsRef.current?.contains(e.target)) {
        setVisibleLessons(false);
      }
    };

    document.addEventListener("click", menuHandler);
    return () => document.removeEventListener("click", menuHandler);
  }, []);

  useEffect(() => {
    if (!id) return;

    Axios.get(`/courses/${id}`).then((res) => {
      if (res.status === 200) {
        setData(res.data || {});
        setIsPreviewPlaying(false);
      }
    });

    Axios.get(`/course_lessons/${id}/list`, authConfig || undefined).then((res) => {
      if (res.status === 200) {
        setLessons(res.data || []);
      }
    });

    if (authConfig) {
      Axios.get(`/users_courses/payed/${id}`, authConfig)
        .then((res) => {
          if (res.status === 200) {
            setIsPayed(!!res.data?.is_payed);
          } else {
            setIsPayed(false);
          }
        })
        .catch(() => {
          setIsPayed(false);
        });

      Axios.get('/auth/me', authConfig)
        .then((res) => {
          if (res.status === 200) {
            setUserRoleID(res.data?.role_id || null);
          } else {
            setUserRoleID(null);
          }
        })
        .catch(() => {
          setUserRoleID(null);
        });
    } else {
      setIsPayed(false);
      setUserRoleID(null);
    }
  }, [id, authConfig]);

  const isPremiumUnlocked =
    isLogin && userRoleID === 5 && Boolean(data?.is_premium_watchable);
  const canWatch = data?.is_free || (isLogin && isPayed) || isPremiumUnlocked;

  useEffect(() => {
    if (canWatch && lessons?.length > 0 && activeIdx === null) {
      setActiveIdx(0);
    }
  }, [canWatch, lessons, activeIdx]);

  const activeLesson = useMemo(() => {
    if (typeof activeIdx === "number") {
      return lessons?.[activeIdx] || null;
    }
    return null;
  }, [activeIdx, lessons]);

  const handlePrimaryAction = () => {
    if (canWatch) {
      if (activeIdx === null && lessons?.length > 0) {
        setActiveIdx(0);
      }
      return;
    }

    if (isLogin) {
      setVisiblePay(true);
    } else {
      setVisibleNotPay(true);
    }
  };

  const handleJoinTelegram = () => {
    window.open("https://t.me/+YdxvPk77i-o1ZTRl", "_blank", "noopener,noreferrer");
  };
  const handleJoinDiscord = () => {
    window.open("https://docs.google.com/forms/d/e/1FAIpQLScByma09QiNtp4yZG5OAbbts70bGipVrV-0Hvl0-NOUe9DWQg/viewform?usp=dialog", "_blank", "noopener,noreferrer");
  };

  const getPreviewVideoURL = (rawValue) => {
    return getPlayableVideoURL(rawValue) || defaultPreviewVideo;
  };
  
  const renderPreviewFeatures = () => {
    return (
      <div className="courseDetailModern__featureGrid">
        <div className="courseDetailModern__featureCard">
          <div className="courseDetailModern__featureIcon">
            <HiOutlineAcademicCap />
          </div>
          <div>
            <h4>Та юу сурах вэ?</h4>
            <p>
              {data?.short_description ||
                "Хичээлийн бүтэц, агуулга, үндсэн сэдвүүдийг системтэйгээр сурах боломжтой."}
            </p>
          </div>
        </div>

        <div className="courseDetailModern__featureCard">
          <div className="courseDetailModern__featureIcon">
            <HiOutlineLockClosed />
          </div>
          <div>
            <h4>Шаардлага</h4>
            <p>
              Та бүртгэлтэй байх шаардлагатай. Худалдан авсны дараа бүх
              хичээлүүд нээгдэнэ.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderStats = () => {
    return (
      <div className="courseDetailModern__statsGrid">
        <div className="courseDetailModern__statCard">
          <strong>{lessons?.length || 0}</strong>
          <span>Хичээл</span>
        </div>
        <div className="courseDetailModern__statCard">
          <strong>{data?.student_count || "10K+"}</strong>
          <span>Суралцагч</span>
        </div>
        <div className="courseDetailModern__statCard">
          <strong>{data?.level || "All level"}</strong>
          <span>Түвшин</span>
        </div>
        <div className="courseDetailModern__statCard courseDetailModern__statCard--wide">
          <strong>{data?.rating || "4.9 ★★★★★"}</strong>
          <span>Сэтгэл ханамж</span>
        </div>
      </div>
    );
  };

  const renderPlayer = () => {
    if (canWatch && activeLesson?.video) {
      const lessonVimeoSrc = getVimeoEmbed(activeLesson.video);

      if (lessonVimeoSrc) {
        return (
          <div className="courseDetailModern__playerHtml">
            <VimeoFrame src={lessonVimeoSrc} title="lesson-player" />
          </div>
        );
      }

      const lessonVideoURL = getPlayableVideoURL(activeLesson.video);

      if (lessonVideoURL && ReactPlayer.canPlay(lessonVideoURL)) {
        return (
          <div className="courseDetailModern__playerHtml">
            <ReactPlayer
              width="100%"
              height="100%"
              url={lessonVideoURL}
              controls
            />
          </div>
        );
      }

      return (
        <div
          className="courseDetailModern__playerHtml"
          dangerouslySetInnerHTML={{
            __html: activeLesson.video,
          }}
        />
      );
    }

    if (canWatch && !activeLesson?.video) {
      return (
        <div className="courseDetailModern__playerFallback">
          Уучлаарай, энэ хичээлийн бичлэг одоогоор байхгүй байна.
        </div>
      );
    }

    const previewVimeoSrc = getVimeoEmbed(data?.intro_video);

    return (
      <div className="courseDetailModern__previewPlayer">
        {previewVimeoSrc ? (
          <VimeoFrame
            src={previewVimeoSrc}
            title="preview-player"
            onPlay={() => setIsPreviewPlaying(true)}
            onPause={() => setIsPreviewPlaying(false)}
            onEnded={() => setIsPreviewPlaying(false)}
          />
        ) : (
          <ReactPlayer
            width="100%"
            height="100%"
            url={getPreviewVideoURL(data?.intro_video)}
            controls
            onPlay={() => setIsPreviewPlaying(true)}
            onPause={() => setIsPreviewPlaying(false)}
            onEnded={() => setIsPreviewPlaying(false)}
          />
        )}

        <div className="courseDetailModern__previewOverlay">
          <div className="courseDetailModern__previewBadge">
            <HiOutlineLockClosed />
            <span>Төлбөртэй сургалт</span>
          </div>

          {!isPreviewPlaying && (
            <div className="courseDetailModern__previewPlay">
              <HiOutlinePlay />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <Header onHome={true} />

      <PayModal id={id} visible={visiblePay} setVisible={setVisiblePay} />

      <Popup
        visible={visibleNotPay}
        messageType="success"
        messageText="Та эхлээд бүртгүүлнэ үү. Бүртгэлтэй бол нэвтэрнэ үү."
        onOk={() => setVisibleNotPay(false)}
        onCancel={() => setVisibleNotPay(false)}
      />

      <div className="courseDetailModern">
        <section className="courseDetailModern__hero">
          <div className="courseDetailModern__bgGlow courseDetailModern__bgGlow--one" />
          <div className="courseDetailModern__bgGlow courseDetailModern__bgGlow--two" />
          <div className="courseDetailModern__bgGrid" />

          <div className="courseDetailModern__container">
            <div className="courseDetailModern__heroGrid">
              <div className="courseDetailModern__mediaCol">
                <div className="courseDetailModern__mediaShell">
                  <div className="courseDetailModern__mediaTopBar">
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </div>

                  <div className="courseDetailModern__heroPlayerWrap">
                    {renderPlayer()}
                  </div>
                </div>
              </div>

              <div className="courseDetailModern__contentCol">
                <div className="courseDetailModern__eyebrow">
                  <span className="courseDetailModern__eyebrowBadge">
                    <HiOutlineSparkles />
                    {data?.is_free ? "Online Free Course" : "Премиум сургалт"}
                  </span>
                </div>

                <h1 className="courseDetailModern__title">
                  {data?.title || "Сургалтын танилцуулга"}
                </h1>

                <p className="courseDetailModern__subtitle">
                  {data?.description_text ||
                    "Чанартай, орчин үеийн, ойлгомжтой бүтэцтэй сургалтаар мэдлэгээ дараагийн түвшинд гаргаарай."}
                </p>

                <div className="courseDetailModern__teacherCard">
                  <img
                    className="courseDetailModern__teacherAvatar"
                    src={data?.teacher_avatar || teacherFallback}
                    alt="teacher"
                    onError={(event) => {
                      event.target.onerror = null;
                      event.target.src = noFile;
                    }}
                  />

                  <div className="courseDetailModern__teacherMeta">
                    <span className="courseDetailModern__teacherLabel">
                      БАГШ
                    </span>
                    <strong className="courseDetailModern__teacherName">
                      Баярсайхан Пүрэв-Очир (Admin)
                    </strong>
                    <span className="courseDetailModern__teacherSub">
                      Мэргэжлийн түвшний тайлбар, шаталсан хөтөлбөр
                    </span>
                  </div>
                </div>

                {isPayed && [2, 3].includes(data?.category_id) && (
                  <>
                    {data?.category_id === 3 && (
                      <button
                        type="button"
                        className="courseDetailModern__telegramBtn"
                        onClick={handleJoinTelegram}
                      >
                        <FaTelegramPlane />
                        Telegram-д нэгдэх
                      </button>
                    )}

                    {data?.category_id === 2 && (
                      <button
                        type="button"
                        className="courseDetailModern__discordBtn"
                        onClick={handleJoinDiscord}
                      >
                        Discord-д нэгдэх
                      </button>
                    )}
                  </>
                )}

                <div className="courseDetailModern__actionRow">
                  
                  {/* СУРГАЛТ АВАХ */}
                  {!canWatch && (
                    <button
                      type="button"
                      className="courseDetailModern__primaryBtn"
                      onClick={handlePrimaryAction}
                    >
                      Сургалт авах
                    </button>
                  )}

                  {/* ЖАГСААЛТ → mobile only, canWatch хамаагүй */}
                  {lessons?.length > 0 && (
                    <button
                      type="button"
                      className="courseDetailModern__ghostBtn courseDetailModern__mobileOnly"
                      onClick={(e) => {
                        e.stopPropagation();
                        // e.nativeEvent.stopImmediatePropagation();
                        setVisibleLessons((prev) => !prev);
                        
                      }}
                    >
                      <HiOutlineClipboardList />
                      Хичээлийн жагсаалт
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="courseDetailModern__contentSection">
          <div className="courseDetailModern__container">
            <div className="courseDetailModern__contentLayout">
              <div className="courseDetailModern__mainContent">
                {/* <div
                  className="courseDetailModern__mobileLessons"
                  ref={lessonsRef}
                >
                  <button
                    type="button"
                    className="courseDetailModern__mobileLessonTrigger"
                    onClick={() => setVisibleLessons((prev) => !prev)}
                  >
                    <HiOutlineClipboardList size={20} />
                    <span>Хичээлийн жагсаалт</span>
                  </button>

                  <div
                    className={`courseDetailModern__mobileLessonList ${
                      visibleLessons ? "show" : ""
                    }`}
                  >
                    {lessons?.map((item, idx) => (
                      <button
                        type="button"
                        key={item.id}
                        className={`courseDetailModern__lessonItem ${
                          activeIdx === idx ? "active" : ""
                        }`}
                        onClick={() => {
                          setActiveIdx(idx);
                          setVisibleLessons(false);
                        }}
                      >
                        <div className="courseDetailModern__lessonIndex">
                          {String(idx + 1).padStart(2, "0")}
                        </div>

                        <div className="courseDetailModern__lessonText">
                          <strong>{item.title}</strong>
                          <span>Хичээл #{idx + 1}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div> */}
                <div className="courseDetailModern__panel">
                  <div className="courseDetailModern__panelHeader">
                    <div>
                      <h3>Сургалтын тухай</h3>
                    </div>
                  </div>

                  <div className="courseDetailModern__aboutBox">
                    <div
                      className="courseDetailModern__richText"
                      dangerouslySetInnerHTML={{
                        __html:
                          data?.description ||
                          "<p>Энэ сургалтаар та сэдвийн үндсэн ойлголтуудыг цэгцтэйгээр сурах боломжтой.</p>",
                      }}
                    />
                  </div>
                  {renderPreviewFeatures()}
                </div>

                {canWatch && activeLesson && (
                  <div className="courseDetailModern__panel">
                    <div className="courseDetailModern__panelHeader">
                      <div>
                        <span className="courseDetailModern__panelKicker">
                          Current lesson
                        </span>
                        <h3>{activeLesson?.title || "Сонгогдсон хичээл"}</h3>
                      </div>

                      <div className="courseDetailModern__lessonBadge">
                        <HiOutlineCheckCircle />
                        Хичээл #{(activeIdx || 0) + 1}
                      </div>
                    </div>

                    <div className="courseDetailModern__lessonInfoBox">
                      Одоогоор үзэж буй хичээл:{" "}
                      <strong>{activeLesson?.title}</strong>
                    </div>
                  </div>
                )}


              </div>

              <aside ref={lessonsRef} className={`courseDetailModern__sidebar ${
                  visibleLessons ? "show" : ""
                }`}>
                <div className="courseDetailModern__panel courseDetailModern__panel--sticky">
                  <div className="courseDetailModern__sidebarHeader">
                    <div>
                      <h3>Хичээлийн жагсаалт</h3>
                    </div>
                    <span>{lessons?.length || 0} хичээл</span>
                  </div>

                  <div className="courseDetailModern__sidebarList">
                    {lessons?.length > 0 ? (
                      lessons.map((item, idx) => (
                        <button
                          type="button"
                          key={item.id}
                          className={`courseDetailModern__lessonItem ${
                            activeIdx === idx ? "active" : ""
                          }`}
                          onClick={() => {
                            setActiveIdx(idx);
                            setVisibleLessons(false); // 🔥 sidebar хаана
                          }}
                        >
                          <div className="courseDetailModern__lessonIndex">
                            {String(idx + 1).padStart(2, "0")}
                          </div>

                          <div className="courseDetailModern__lessonText">
                            <strong>{item.title}</strong>
                            <span>Хичээл #{idx + 1}</span>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="courseDetailModern__emptyLessons">
                        Хичээлийн жагсаалт хоосон байна.
                      </div>
                    )}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default CourseDetail;
